import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import { GET_SCHEMA_TREE } from '@atom/graph/schema';
import { List, Modal, Progress } from '@atom/mui';
import { AssetElementsCreateInput } from '@atom/types/asset';
import { SchemaTree } from '@atom/types/schema';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import {
  buildElementsCreateInput,
  ElementSelection,
  setQuantity,
  toggleSelection,
} from './addElementsModalUtilities';
import ElementSelectionBranch from './ElementSelectionBranch';
import ElementSelectionContext, {
  ElementSelectionContextValue,
} from './ElementSelectionContext';

import classes from './addElementsModal.module.css';

const LOAD_ERROR_TEXT = 'Unable to load elements. Please try again.';

type AddElementsModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  assetId: string;
  assetName: string;
  schemaId: string;
  onBatchCreate: (input: AssetElementsCreateInput) => void;
  confirmDataCy?: string;
  cancelDataCy?: string;
};

const AddElementsModal = ({
  open,
  onClose,
  title = 'Add elements to asset',
  assetId,
  assetName,
  schemaId,
  onBatchCreate,
  confirmDataCy = 'addElementsModalConfirmButton',
  cancelDataCy = 'addElementsModalCancelButton',
}: AddElementsModalProps) => {
  const [selection, setSelection] = useState<ElementSelection>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data, error } = useQuery(GET_SCHEMA_TREE, {
    variables: { id: schemaId },
    skip: !open || !schemaId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!open) {
      setSelection({});
      setExpanded(new Set());
    }
  }, [open]);

  const schemaTree = data?.schemaTree;
  const elements = schemaTree?.elements ?? [];
  const elementGroups = schemaTree?.elementGroups ?? [];
  const hasElements = elements.length > 0 || elementGroups.length > 0;

  const contextValue: ElementSelectionContextValue = useMemo(
    () => ({
      selection,
      expanded,
      onToggle: (element: SchemaTree, path: string[]) =>
        setSelection(current => toggleSelection(current, element, path)),
      onQuantityChange: (path: string[], value: string) =>
        setSelection(current => setQuantity(current, path, value)),
      onToggleExpanded: (key: string) =>
        setExpanded(current => toggleFromSet(current, key)),
    }),
    [selection, expanded],
  );

  const confirm = () => {
    onBatchCreate(
      buildElementsCreateInput({
        assetId,
        assetSchemaId: schemaId,
        schemaTree,
        selection,
      }),
    );
    onClose();
  };

  if (schemaTree && !hasElements) {
    return (
      <Modal
        title="No elements available."
        open={open}
        onCancel={onClose}
        cancelButtonText="OK"
        CancelButtonProps={{ 'data-cy': cancelDataCy }}
      >
        There are no elements under
        <span className={classes.assetName}>{assetName}</span>
      </Modal>
    );
  }

  return (
    <Modal
      title={title}
      open={open}
      width="lg"
      onConfirm={confirm}
      onCancel={onClose}
      confirmButtonText="Add"
      disabled={R.isEmpty(selection)}
      contentStyle={{ padding: 0 }}
      ConfirmButtonProps={{ 'data-cy': confirmDataCy }}
      CancelButtonProps={{ 'data-cy': cancelDataCy }}
    >
      {error ? (
        <div className={classes.errorMessage} data-cy="addElementsModalError">
          {LOAD_ERROR_TEXT}
        </div>
      ) : !schemaTree ? (
        <div className={classes.spinnerContainer}>
          <Progress />
        </div>
      ) : (
        <div className={classes.container}>
          <div className={classes.subheading}>
            Select elements and quantities from inventory type for
            <span className={classes.assetName}>{assetName}</span>
          </div>
          <ElementSelectionContext.Provider value={contextValue}>
            <List disablePadding>
              <ElementSelectionBranch
                elements={elements}
                groups={elementGroups}
                parentPath={[]}
                depth={0}
              />
            </List>
          </ElementSelectionContext.Provider>
        </div>
      )}
    </Modal>
  );
};

export default AddElementsModal;
